import { translate } from "app/language/service"
import { getPercent } from "app/tools/common"
import { CampaignAPI, ForecastAPI, LookupAPI } from "generated-api"
import { normalizeSync } from "normalize-diacritics"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import {
  IEnvironment,
  IEnvironmentAssociation,
  IEnvironmentDTO,
  IEnvironmentData,
  IEnvironmentRepository,
  IEnvironmentsForecast,
  OTHER_ENVIRONMENT_GROUP_ID
} from "./model"

export const environmentsRPO: IEnvironmentRepository = {
  getAllEnvironments(): Promise<IEnvironmentData> {
    return new Promise<IEnvironmentData>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiVenueTypeList()
          .then(response => {
            const list = response.sort((e1, e2) => {
              const isE2DmiCodeEmpty = typeof e2.dmi_code !== "string" || e2.dmi_code === ""
              if (typeof e1.dmi_code !== "string" || e1.dmi_code === "") {
                return isE2DmiCodeEmpty ? 0 : 1
              } else if (isE2DmiCodeEmpty) {
                return -1
              }
              return e1.dmi_code < (e2.dmi_code as string) ? -1 : 1
            })
            resolve(getEnvironmentData(list))
          })
          .catch((error: any) => {
            console.error("Server error while getting all environments", error)
            reject("env.get.all.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all environments", ex)
        reject("env.get.all.error.unexpected")
      }
    })
  },
  getEnvironmentsByName(name: string): Promise<IEnvironment[]> {
    return new Promise<IEnvironment[]>((resolve, reject) => {
      const lowerCaseName = normalizeSync(name).toLowerCase()
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiVenueTypeList()
          .then(response =>
            resolve(
              response
                .filter(e => normalizeSync(e.name).toLowerCase().includes(lowerCaseName))
                .map(e => ({
                  ...e,
                  children: [],
                  parent: null,
                  treeDepth: 0,
                  dmi_code: ""
                }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while getting environments by name", error)
            reject("env.get.by.name.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting environments by name", ex)
        reject("env.get.by.name.error.unexpected")
      }
    })
  },
  getEnvironmentsById(ids: number[]): Promise<IEnvironment[]> {
    return new Promise<IEnvironment[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiVenueTypeList(undefined, undefined, undefined, undefined, undefined, undefined, ids)
          .then(response =>
            resolve(
              response.map(e => ({
                ...e,
                children: [],
                parent: null,
                treeDepth: 0,
                dmi_code: ""
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while getting environments by ids", error)
            reject("env.get.by.ids.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting environments by ids", ex)
        reject("env.get.by.ids.error.unexpected")
      }
    })
  },
  getCampaignEnvironments(campaignId: number): Promise<IEnvironmentAssociation[]> {
    return new Promise<IEnvironmentAssociation[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignVenuetypeList(campaignId)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting all environments associations", error)
            reject("env.get.campaign.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all environments associations", ex)
        reject("env.get.campaign.error.unexpected")
      }
    })
  },
  associateEnvironment(campaignId: number, envId: number): Promise<IEnvironmentAssociation> {
    return new Promise<IEnvironmentAssociation>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignVenuetype({ campaign_id: campaignId, venue_type_id: envId })
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while associating environment", error)
            reject("env.associate.error.server")
          })
      } catch (ex) {
        console.error("Server error while associating environment", ex)
        reject("env.associate.error.unexpected")
      }
    })
  },
  setEnvironments(campaignId: number, venue_type_ids: number[]): Promise<IEnvironmentAssociation[]> {
    return new Promise<IEnvironmentAssociation[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignVenuetypeList(
          campaignId,
          venue_type_ids.map(venue_type_id => ({ campaign_id: campaignId, venue_type_id }))
        )
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while settings campaign environments", error)
            reject("env.set.error.server")
          })
      } catch (ex) {
        console.error("Server error while settings campaign environments", ex)
        reject("env.set.error.unexpected")
      }
    })
  },
  unassociateEnvironment(campaignId: number, associationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignVenuetype(campaignId, associationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while unassociating environment", error)
            reject("env.unassociate.error.server")
          })
      } catch (ex) {
        console.error("Server error while unassociating environment", ex)
        reject("env.unassociate.error.unexpected")
      }
    })
  },
  getEnvironmentForecast(campaignId: number): Promise<IEnvironmentsForecast> {
    return new Promise<IEnvironmentsForecast>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        ForecastAPI.getApiForecastDoohVenueType(campaignId)
          .then(response => {
            const backendForecast = response
            const forecast: IEnvironmentsForecast = {}
            const groups = backendForecast.groups || []
            for (const group of groups) {
              forecast[typeof group.venue_type_id === "number" ? group.venue_type_id : -1] = {
                screen: group.screen_count,
                screenTotal: backendForecast.total_screens,
                screenPercent: getPercent(group.screen_count, backendForecast.total_screens),
                impression: group.impressions,
                impressionTotal: backendForecast.total_impressions,
                impressionPercent: getPercent(group.impressions, backendForecast.total_impressions),
                spendInCad: group.worth_cad,
                spendTotalInCad: backendForecast.total_spend
              }
            }
            resolve(forecast)
          })
          .catch((error: any) => {
            console.error("Server error while getting environments forecast", error)
            reject("env.get.forecast.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting environments forecast", ex)
        reject("env.get.forecast.error.unexpected")
      }
    })
  }
}

function getEnvironmentData(environmentDTOList: IEnvironmentDTO[]): IEnvironmentData {
  const environmentById: { [environmentId: number]: IEnvironment } = {}
  const list: IEnvironment[] = []
  const tree: IEnvironment[] = []
  const foundDmiCodes: string[] = []
  let duplicateDmiCodeSuffix: string = ""
  let currentParentByDepth: IEnvironment[] = []
  const otherEnvironmentGroup: IEnvironment = {
    id: OTHER_ENVIRONMENT_GROUP_ID,
    dmi_code: "",
    name: translate("label.other"),
    openooh_code: null,
    openrtb3_code: null,
    description: "",
    parent: null,
    ssp_id: null,
    treeDepth: 0,
    children: []
  }
  for (const environmentDTO of environmentDTOList) {
    const currentDepth =
      (environmentDTO.dmi_code !== "" && environmentDTO.dmi_code !== null
        ? environmentDTO.dmi_code.split(".").length
        : 1) - 1
    const environment: IEnvironment = {
      ...environmentDTO,
      dmi_code: environmentDTO.dmi_code || "",
      children: [],
      parent: null,
      treeDepth: currentDepth
    }
    list.push(environment)
    if (environment.dmi_code !== "") {
      if (foundDmiCodes.includes(environment.dmi_code)) {
        console.warn(`VENUE_TYPE DMI_CODE DUPLICATE FOUND "${environment.dmi_code}" (env_id="${environment.id}")`)
        duplicateDmiCodeSuffix += "9"
        environment.dmi_code += duplicateDmiCodeSuffix
      }
      foundDmiCodes.push(environment.dmi_code)
    }
    environmentById[environment.id] = environment
    if (currentDepth === 0) {
      if (environmentDTO.dmi_code === "") {
        otherEnvironmentGroup.children.push(environment)
        environment.parent = otherEnvironmentGroup
        environment.treeDepth = 1
      } else {
        tree.push(environment)
        currentParentByDepth = []
      }
    } else {
      const parent = currentParentByDepth[currentDepth - 1]
      if (!parent) {
        console.error(`Unable to find parent of environment id="${environment.id}" dmi_code="${environment.dmi_code}"`)
      } else {
        parent.children.push(environment)
        environment.parent = parent
      }
    }
    currentParentByDepth[currentDepth] = environment
  }
  if (otherEnvironmentGroup.children.length > 0) {
    tree.push(otherEnvironmentGroup)
  }
  return {
    list,
    tree,
    environmentById
  }
}

/* Example of forecast result
{
  "total_screens": 198175,
  "total_impressions": 210535467927.09317,
  "total_spend": 1979759174.6985524,
  "groups": [
    {
      "screen_count": 1238,
      "impressions": 208194107.61620373,
      "worth_cad": 1683344.708853016,
      "venue_type_id": 178
    },
    ...
  ]
}
*/
